import {useCallback} from "react";
import {FileRejection, useDropzone} from "react-dropzone";
import {ArrowUpTrayIcon, DocumentTextIcon, XMarkIcon} from "@heroicons/react/24/outline";

interface JsonDropzoneProps {
    file: File | null;
    onSelect: (file: File) => void;
    onClear: () => void;
    disabled?: boolean;
}

const MAX_SIZE_BYTES = 20 * 1024 * 1024;

export function JsonDropzone({file, onSelect, onClear, disabled}: JsonDropzoneProps) {
    const onDropAccepted = useCallback(
        (files: File[]) => {
            if (files[0]) onSelect(files[0]);
        },
        [onSelect],
    );

    const dropzone = useDropzone({
        onDropAccepted,
        onDropRejected: undefined,
        accept: {"application/json": [".json"]},
        maxFiles: 1,
        maxSize: MAX_SIZE_BYTES,
        disabled,
    });

    if (file) {
        return (
            <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6 text-gray-500"/>
                    <div>
                        <span className="font-medium">{file.name}</span>
                        <span className="ml-2 text-xs text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                        </span>
                    </div>
                </div>
                {!disabled && (
                    <button
                        type="button"
                        aria-label="Bestand verwijderen"
                        className="h-8 w-8 hover:bg-gray-100 rounded-full p-1"
                        onClick={onClear}
                    >
                        <XMarkIcon className="text-gray-500"/>
                    </button>
                )}
            </div>
        );
    }

    return (
        <div
            {...dropzone.getRootProps({
                className: `flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 ${
                    dropzone.isDragActive ? "bg-primary text-white" : ""
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`,
            })}
        >
            <input {...dropzone.getInputProps()} />
            <ArrowUpTrayIcon className="w-8 h-8 mb-3 text-gray-500"/>
            {dropzone.isDragActive ? (
                <p className="text-lg text-white">Laat het bestand hier los</p>
            ) : (
                <>
                    <p className="mb-1 text-sm text-gray-500">
                        <span className="font-semibold">Klik om te uploaden</span> of sleep een
                        JSON-bestand hierheen
                    </p>
                    <p className="text-xs text-gray-500">JSON</p>
                </>
            )}
        </div>
    );
}
